import { DOMESTIC_NUTRITION_KEYS } from "../../../Utils/constants"
import NUTRIENTS_VALUES from "../../../Utils/mandatoryNutrientAgeGroup"
import config from "../../../config"
import {
  BaseLabelJSON,
  Unit,
  AgeGroup,
  NonMandatoryNutrient,
} from "../../../constants/exportsCountries"
import nonMandatoryKeyMapping from "../../../schema/mapping/non_mandatory_key_mapping.json"
import {
  INDIA_NO_RDA_NUTRIENT_KEYS,
  IndiaNutrientData,
  IndiaNutritionTableValue,
  MandatoryNutrientObject,
} from "./IndiaTypes"

export const calculateIndiaNutritionTableValues = (
  label_json: BaseLabelJSON,
): IndiaNutritionTableValue => {
  const {
    serving_size,
    serving_size_unit,
    net_quantity,
    net_quantity_unit,
    age_group,
    mandatory_nutrient,
    non_mandatory_nutrient,
  } = label_json

  let show_rda_value_in_decimal = false

  const user_details_string = localStorage.getItem("User_Details")
  if (user_details_string) {
    const userDetails = JSON.parse(user_details_string)
    if (config.ALLOWED_PERCENT_RDA_IN_DECIMAL.includes(userDetails.email)) {
      show_rda_value_in_decimal = true
    }
  }

  const no_of_servings = calculateNoOfServings(
    serving_size,
    serving_size_unit,
    net_quantity,
    net_quantity_unit,
  )

  const serving_size_in_base_unit = convertQuantityToBaseUnit(
    serving_size,
    serving_size_unit,
  )

  const filtered_mandatory_nutrient: MandatoryNutrientObject =
    removeEmptyStringValuesFromObject(mandatory_nutrient[0])

  const mandatory_nutrient_calculated_values = getIndiaMandatoryNutrientData(
    filtered_mandatory_nutrient,
    serving_size_in_base_unit,
    age_group,
    show_rda_value_in_decimal,
  )
  const non_mandatory_nutrient_calculated_values =
    getIndiaNonMandatoryNutrientData(
      non_mandatory_nutrient,
      serving_size_in_base_unit,
      age_group,
      show_rda_value_in_decimal,
    )

  const sorted_non_mandatory_nutrient_calculated_values = sortNutrientsByName(
    non_mandatory_nutrient_calculated_values,
  )

  return {
    no_of_servings: no_of_servings,
    serving_size: serving_size ?? "",
    serving_size_unit: serving_size_unit ?? "",
    age_group,
    mandatory_nutrient: mandatory_nutrient_calculated_values,
    non_mandatory_nutrient: sorted_non_mandatory_nutrient_calculated_values,
  }
}

function sortNutrientsByName(
  nutrients: IndiaNutrientData[],
): IndiaNutrientData[] {
  return [...nutrients].sort((a, b) => a.name.localeCompare(b.name))
}

export function getIndiaNonMandatoryNutrientData(
  non_mandatory_nutrient: NonMandatoryNutrient,
  serving_size_in_base_unit: number,
  age_group: AgeGroup,
  show_rda_value_in_decimal: boolean = false,
): IndiaNutrientData[] {
  return non_mandatory_nutrient
    ?.filter(
      (nutrient) =>
        nutrient.name?.trim() &&
        nutrient.quantity !== undefined &&
        nutrient.quantity !== null &&
        nutrient.quantity.trim() !== "",
    )
    .map((nutrient) => {
      return {
        key: nutrient.name,
        name:
          getNameOfIndiaOtherNutrientByNutrientKey(nutrient.name) ??
          nutrient.name,
        ...calculatedAndRoundOffIndianNutrientPer100PerServeAndRDAValue(
          nutrient.name,
          nutrient.quantity ?? "",
          serving_size_in_base_unit,
          age_group,
          show_rda_value_in_decimal,
        ),
      }
    })
}

export function getNameOfIndiaOtherNutrientByNutrientKey(
  nutrient_key: string,
): string {
  if (Array.isArray(nonMandatoryKeyMapping)) {
    const found = nonMandatoryKeyMapping.find(
      (nutrient: { key: string; value: string }) =>
        nutrient?.key?.toLowerCase() === nutrient_key?.toLowerCase(),
    )
    return found?.value ?? nutrient_key
  }
  return nutrient_key
}

export function getIndiaMandatoryNutrientData(
  filtered_mandatory_nutrient: MandatoryNutrientObject,
  serving_size_in_base_unit: number,
  age_group: AgeGroup,
  show_rda_value_in_decimal: boolean = false,
): IndiaNutrientData[] {
  return [
    {
      key: DOMESTIC_NUTRITION_KEYS.ENERGY,
      name: "Energy (kcal)",
      ...calculatedAndRoundOffIndianNutrientPer100PerServeAndRDAValue(
        DOMESTIC_NUTRITION_KEYS.ENERGY,
        filtered_mandatory_nutrient[DOMESTIC_NUTRITION_KEYS.ENERGY] ?? "",
        serving_size_in_base_unit,
        age_group,
        show_rda_value_in_decimal,
      ),
    },
    {
      key: DOMESTIC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE,
      name: "Total Carbohydrate (g)",
      ...calculatedAndRoundOffIndianNutrientPer100PerServeAndRDAValue(
        DOMESTIC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE,
        filtered_mandatory_nutrient[
          DOMESTIC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE
        ] ?? "",
        serving_size_in_base_unit,
        age_group,
        show_rda_value_in_decimal,
      ),
    },
    {
      name: "Total Sugars (g)",
      key: DOMESTIC_NUTRITION_KEYS.TOTAL_SUGARS,
      ...calculatedAndRoundOffIndianNutrientPer100PerServeAndRDAValue(
        DOMESTIC_NUTRITION_KEYS.TOTAL_SUGARS,
        filtered_mandatory_nutrient[DOMESTIC_NUTRITION_KEYS.TOTAL_SUGARS] ?? "",
        serving_size_in_base_unit,
        age_group,
        show_rda_value_in_decimal,
      ),
    },
    {
      name: "Added Sugars (g)",
      key: DOMESTIC_NUTRITION_KEYS.ADDED_SUGARS,
      ...calculatedAndRoundOffIndianNutrientPer100PerServeAndRDAValue(
        DOMESTIC_NUTRITION_KEYS.ADDED_SUGARS,
        filtered_mandatory_nutrient[DOMESTIC_NUTRITION_KEYS.ADDED_SUGARS] ?? "",
        serving_size_in_base_unit,
        age_group,
        show_rda_value_in_decimal,
      ),
    },
    {
      name: "Protein (g)",
      key: DOMESTIC_NUTRITION_KEYS.PROTEIN,
      ...calculatedAndRoundOffIndianNutrientPer100PerServeAndRDAValue(
        DOMESTIC_NUTRITION_KEYS.PROTEIN,
        filtered_mandatory_nutrient[DOMESTIC_NUTRITION_KEYS.PROTEIN] ?? "",
        serving_size_in_base_unit,
        age_group,
        show_rda_value_in_decimal,
      ),
    },
    {
      name: "Total Fat (g)",
      key: DOMESTIC_NUTRITION_KEYS.TOTAL_FAT,
      ...calculatedAndRoundOffIndianNutrientPer100PerServeAndRDAValue(
        DOMESTIC_NUTRITION_KEYS.TOTAL_FAT,
        filtered_mandatory_nutrient[DOMESTIC_NUTRITION_KEYS.TOTAL_FAT] ?? "",
        serving_size_in_base_unit,
        age_group,
        show_rda_value_in_decimal,
      ),
    },
    // only render saturated fat if data exist
    ...(filtered_mandatory_nutrient[DOMESTIC_NUTRITION_KEYS.SATURATED_FAT]
      ? [
          {
            name: "Saturated Fat (g) (not more than)",
            key: DOMESTIC_NUTRITION_KEYS.SATURATED_FAT,
            ...calculatedAndRoundOffIndianNutrientPer100PerServeAndRDAValue(
              DOMESTIC_NUTRITION_KEYS.SATURATED_FAT,
              filtered_mandatory_nutrient[
                DOMESTIC_NUTRITION_KEYS.SATURATED_FAT
              ] ?? "",
              serving_size_in_base_unit,
              age_group,
              show_rda_value_in_decimal,
            ),
          },
        ]
      : []),

    // only render Trans fat if data exist
    ...(filtered_mandatory_nutrient[DOMESTIC_NUTRITION_KEYS.TRANS_FAT]
      ? [
          {
            name: "Trans Fat (g) (not more than)",
            key: DOMESTIC_NUTRITION_KEYS.TRANS_FAT,
            ...calculatedAndRoundOffIndianNutrientPer100PerServeAndRDAValue(
              DOMESTIC_NUTRITION_KEYS.TRANS_FAT,
              filtered_mandatory_nutrient[DOMESTIC_NUTRITION_KEYS.TRANS_FAT] ??
                "",
              serving_size_in_base_unit,
              age_group,
              show_rda_value_in_decimal,
            ),
          },
        ]
      : []),
    // only render Cholesterol if data exist
    ...(filtered_mandatory_nutrient[DOMESTIC_NUTRITION_KEYS.CHOLESTEROL]
      ? [
          {
            name: "Cholesterol (mg)",
            key: DOMESTIC_NUTRITION_KEYS.CHOLESTEROL,
            ...calculatedAndRoundOffIndianNutrientPer100PerServeAndRDAValue(
              DOMESTIC_NUTRITION_KEYS.CHOLESTEROL,
              filtered_mandatory_nutrient[
                DOMESTIC_NUTRITION_KEYS.CHOLESTEROL
              ] ?? "",
              serving_size_in_base_unit,
              age_group,
              show_rda_value_in_decimal,
            ),
          },
        ]
      : []),
    {
      name: "Sodium (mg)",
      key: DOMESTIC_NUTRITION_KEYS.SODIUM,
      ...calculatedAndRoundOffIndianNutrientPer100PerServeAndRDAValue(
        DOMESTIC_NUTRITION_KEYS.SODIUM,
        filtered_mandatory_nutrient[DOMESTIC_NUTRITION_KEYS.SODIUM] ?? "",
        serving_size_in_base_unit,
        age_group,
        show_rda_value_in_decimal,
      ),
    },
  ]
}

export function calculatedAndRoundOffIndianNutrientPer100PerServeAndRDAValue(
  nutrient_key: string,
  per_100g: string,
  serving_size_in_base_unit: number,
  age_group: AgeGroup,
  show_rda_value_in_decimal: boolean = false,
): { per_100g: string; per_serve: string; rda: string } {
  const calculated_per_100g = calculateAndRoundOffIndiaNutrientPer100Value(
    nutrient_key,
    per_100g,
  )

  const calculated_per_serve = calculateAndRoundOffIndiaNutrientPerServeValue(
    nutrient_key,
    per_100g,
    serving_size_in_base_unit,
  )

  const calculated_rda = calculateAndRoundOffIndiaNutrientRDAValue(
    nutrient_key as keyof (typeof NUTRIENTS_VALUES)[AgeGroup],
    per_100g,
    serving_size_in_base_unit,
    age_group,
    show_rda_value_in_decimal,
  )

  return {
    per_100g: calculated_per_100g,
    per_serve: calculated_per_serve,
    rda: calculated_rda,
  }
}

export function calculateAndRoundOffIndiaNutrientPer100Value(
  nutrient_key: string,
  per_100g: string,
): string {
  const parse_value = parseFloat(per_100g)

  if (isNaN(parse_value) || !nutrient_key) {
    return per_100g
  }

  // Energy value roundoff to whole number
  if (nutrient_key === DOMESTIC_NUTRITION_KEYS.ENERGY) {
    return parse_value.toFixed(0)
  }

  // Trans fat value roundoff to 2 decimals
  if (nutrient_key === DOMESTIC_NUTRITION_KEYS.TRANS_FAT) {
    return parse_value.toFixed(2)
  }

  //All remaining nutrient roundoff to 1 decimal
  return parse_value.toFixed(1)
}

export function calculateAndRoundOffIndiaNutrientPerServeValue(
  nutrient_key: string,
  per_100g: string,
  serving_size_in_base_unit: number,
): string {
  const parse_value = (parseFloat(per_100g) * serving_size_in_base_unit) / 100

  if (isNaN(parse_value) || !nutrient_key) {
    return per_100g
  }

  // Energy value roundoff to whole number
  if (nutrient_key === DOMESTIC_NUTRITION_KEYS.ENERGY) {
    return parse_value.toFixed(0)
  }

  // Trans fat value roundoff to 2 decimals
  if (nutrient_key === DOMESTIC_NUTRITION_KEYS.TRANS_FAT) {
    return parse_value.toFixed(2)
  }

  //All remaining nutrient roundoff to 1 decimal
  return parse_value.toFixed(1)
}

export function calculateAndRoundOffIndiaNutrientRDAValue(
  nutrient_key: keyof (typeof NUTRIENTS_VALUES)[AgeGroup],
  per_100g: string,
  serving_size_in_base_unit: number,
  age_group: AgeGroup,
  show_rda_value_in_decimal: boolean = false,
): string {
  if (INDIA_NO_RDA_NUTRIENT_KEYS.includes(nutrient_key) || !per_100g) {
    return "-"
  }

  serving_size_in_base_unit =
    typeof serving_size_in_base_unit === "string"
      ? parseFloat(serving_size_in_base_unit)
      : serving_size_in_base_unit

  const parse_per_100g_value = parseFloat(per_100g)

  const rda_value_by_age_group: number =
    NUTRIENTS_VALUES?.[age_group]?.[nutrient_key]

  if (!rda_value_by_age_group || isNaN(rda_value_by_age_group)) {
    return "-"
  }

  const percentDV =
    parse_per_100g_value * (serving_size_in_base_unit / rda_value_by_age_group)

  if (isNaN(percentDV)) {
    return "-"
  }

  if (show_rda_value_in_decimal) {
    return percentDV.toFixed(1)
  }

  return percentDV.toFixed(0)
}

export function calculateNoOfServings(
  serving_size: string | null | undefined,
  serving_size_unit: Unit | null | undefined,
  net_quantity: string | null | undefined,
  net_quantity_unit: Unit | null | undefined,
): number | "" {
  if (
    !serving_size ||
    !serving_size_unit ||
    !net_quantity ||
    !net_quantity_unit
  ) {
    return ""
  }

  const serving_size_num = parseFloat(serving_size)
  const net_quantity_num = parseFloat(net_quantity)

  if (isNaN(serving_size_num) || isNaN(net_quantity_num)) {
    return ""
  }

  const serving_size_in_base_unit = convertQuantityToBaseUnit(
    serving_size_num,
    serving_size_unit,
  )
  const net_quantity_in_base_unit = convertQuantityToBaseUnit(
    net_quantity_num,
    net_quantity_unit,
  )

  const number_of_servings =
    net_quantity_in_base_unit / serving_size_in_base_unit
  const fraction = number_of_servings - Math.floor(number_of_servings)

  if (isFinite(number_of_servings) === false || number_of_servings <= 0) {
    return ""
  }
  return fraction >= 0.6
    ? Math.ceil(number_of_servings)
    : Math.floor(number_of_servings)
}

export function convertQuantityToBaseUnit(
  quantity: number | string,
  unit: string,
): number {
  const parsed_quantity =
    typeof quantity === "string" ? parseFloat(quantity) : quantity

  if (isNaN(parsed_quantity) || !unit || !quantity) {
    return 0
  }

  switch (unit?.toLowerCase()) {
    case "kg":
    case "l":
      return parsed_quantity * 1000
    case "g":
    case "ml":
      return parsed_quantity
    default:
      return parsed_quantity
  }
}

export function removeEmptyStringValuesFromObject(
  data: Record<string, string>,
): Record<string, string> {
  const cleaned: Record<string, string> = {}

  for (const key in data) {
    if (data[key] !== "") {
      cleaned[key] = data[key]
    }
  }

  return cleaned
}
